interface Event {
  id: number;
  title: string;
  sport: string;
  date: string;
  endDate?: string;
  time: string;
  location: string;
  participants: number;
  maxParticipants: number;
  registrationFee: number;
  categories: string[];
  organizer?: string;
  genderCategories?: string[];
  subcategories?: Record<string, string[]>;
  matchType?: 'singles' | 'dobles' | 'ambos';
  isLeague?: boolean;
}

export const getUpcomingEvents = (events: Event[], limit: number = 6): Event[] => {
  const now = new Date();
  const upcomingEvents = events
    .filter(event => {
      const eventDate = new Date(event.date);
      return eventDate >= now;
    })
    .sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateA.getTime() - dateB.getTime();
    });

  return upcomingEvents.slice(0, limit);
};

export const convertEventToSuggestedFormat = (event: Event) => ({
  id: event.id,
  title: event.title,
  organizer: event.organizer || 'Organizador no especificado',
  sport: event.sport,
  startDate: event.date,
  endDate: event.endDate || event.date,
  time: event.time,
  location: event.location,
  category: event.categories?.[0] || 'General',
  partner: undefined,
  status: 'upcoming' as const
});

export interface TournamentEvent {
  id: number | string;
  title: string;
  organizer: string;
  sport: string;
  startDate: string;
  endDate: string;
  time: string;
  location: string;
  category: string;
  subcategory?: string;
  status: "upcoming" | "active" | "finished";
  matchType?: "singles" | "doubles";
  tournamentFormat?: 'round-robin' | 'sumatoria-juegos' | 'eliminacion-directa';
  partner?: string;
  userName?: string;
  currentStage?: string;
  nextMatch?: {
    opponent: string;
    court: string;
    time: string;
  };
  registrationFee?: number;
  participants?: number;
  maxParticipants?: number;
}

export const getTournamentById = (id: number, mockEvents: TournamentEvent[]): TournamentEvent | null => {
  return mockEvents.find(event => event.id === id) || null;
};

export const getNextMatchInfo = (tournament: TournamentEvent | null) => {
  if (!tournament?.nextMatch) return null;
  
  return {
    opponent: tournament.nextMatch.opponent,
    court: tournament.nextMatch.court,
    time: tournament.nextMatch.time,
    date: tournament.startDate
  };
};

export const formatNextMatchDisplay = (nextMatch: ReturnType<typeof getNextMatchInfo>) => {
  if (!nextMatch) return null;
  
  return {
    opponent: nextMatch.opponent,
    court: nextMatch.court,
    time: nextMatch.time,
    date: nextMatch.date,
    displayText: `vs ${nextMatch.opponent}`,
    courtText: `Cancha ${nextMatch.court}`,
    timeText: nextMatch.time
  };
};

/**
 * Normalize a subcategory by stripping the category prefix.
 * e.g. normalizeSubcategory("Masculino A", "masculino") => "A"
 *      normalizeSubcategory("B2", "masculino") => "B2"
 */
export const normalizeSubcategory = (subcategory: string, category: string): string => {
  if (!subcategory || !category) return subcategory || '';
  const lowerSub = subcategory.toLowerCase();
  const lowerCat = category.toLowerCase();
  if (lowerSub.startsWith(lowerCat + ' ')) {
    return subcategory.substring(category.length).trim();
  }
  return subcategory;
};

/**
 * Find tennis subcategory details from rules, checking both root and ladderConfig paths.
 * Returns the array of detail objects for the matching category, or undefined.
 */
const findTennisSubcategoryDetails = (rules: any, category: string): any[] | undefined => {
  if (!rules || !category) return undefined;

  // Check both paths: root and ladderConfig
  const paths = [
    rules?.tennisCategories?.subcategoryDetails,
    rules?.ladderConfig?.tennisCategories?.subcategoryDetails,
  ];

  for (const subcategoryDetails of paths) {
    if (!subcategoryDetails) continue;
    const detailKeys = Object.keys(subcategoryDetails);
    const matchingKey = detailKeys.find(k => k.toLowerCase() === category.toLowerCase());
    if (matchingKey) {
      const details = subcategoryDetails[matchingKey];
      if (Array.isArray(details)) return details;
    }
  }

  return undefined;
};

/**
 * Get the maximum participants allowed for a specific subcategory
 * Handles eventRules as both string (JSON) or object
 */
export const getSubcategoryMaxParticipants = (
  eventRules: any,
  category: string,
  subcategory: string
): number | null => {
  try {
    let rules = eventRules;
    if (typeof eventRules === 'string') {
      try {
        rules = JSON.parse(eventRules);
      } catch (parseError) {
        console.error('Error parsing eventRules string:', parseError);
        return null;
      }
    }

    // Canonical subcategory (strip category prefix for matching)
    const canonicalSub = normalizeSubcategory(subcategory, category);

    // 1) Try tennis subcategory details (both root and ladderConfig)
    const tennisDetails = findTennisSubcategoryDetails(rules, category);
    if (tennisDetails) {
      const match = tennisDetails.find((d: any) => {
        const label = d.customName || d.name || '';
        return label === subcategory || label === canonicalSub
          || normalizeSubcategory(label, category) === canonicalSub;
      });
      if (match?.maxRegistrations) return match.maxRegistrations;
    }

    // 2) Try genderCategories path
    if (rules?.genderCategories) {
      const categoryData = rules.genderCategories[category];
      if (Array.isArray(categoryData)) {
        const subcategoryData = categoryData.find(
          (cat: any) => cat.category === subcategory || cat.category === canonicalSub
        );
        if (subcategoryData?.maxParticipants) return subcategoryData.maxParticipants;
      }
    }

    return null;
  } catch (error) {
    console.error('Error getting subcategory max participants:', error);
    return null;
  }
};

/**
 * Calculate guaranteed matches based on participants per group from tournament rules
 */
export const calculateGuaranteedMatches = (
  eventFormat: string | undefined,
  isLeague: boolean,
  participants: number,
  maxParticipants: number,
  matchType?: 'singles' | 'dobles' | 'ambos',
  playersPerGroup?: number
): number => {
  if (playersPerGroup && playersPerGroup > 1) {
    return playersPerGroup - 1;
  }

  const totalParticipants = Math.max(participants, 8);
  
  if (isLeague) {
    switch (eventFormat) {
      case 'round-robin':
        const teamsInGroup = matchType === 'singles' ? Math.min(totalParticipants, 8) : Math.min(totalParticipants / 2, 8);
        return Math.max(teamsInGroup - 1, 3);
      case 'escalera-piramidal':
        return 10;
      case 'sumatoria-juegos':
        return 6;
      default:
        return 5;
    }
  } else {
    switch (eventFormat) {
      case 'round-robin':
        const groupSize = 4;
        return Math.max(groupSize - 1, 3);
      case 'eliminacion-directa':
        return 2;
      case 'sumatoria-juegos':
        return 4;
      default:
        return 3;
    }
  }
};
